import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/reducers';
import * as StorageBrowserStore from '../../../redux/middleware/StorageBrowser.Middleware';
import { UploadSasToken } from '../../../models/Entities/AzureFiles/AzureFileUpload';
import { ErrorType } from '../../../models/HttpError';
import { StorageEntityRef, StorageOwnerNames } from '../../../functions/StorageBrowser.Functions';
import { selectStorageOwnerNames } from '../FilesExplorer/FilesExplorer.Functions';

//#region Props

/** File shown in the preview (path relative to the tenant root) */
export interface PreviewFile {
  /** Path relative to the tenant root; for a contract file without blob, a synthetic `contract-file:{id}` */
  path: string;
  /** Contract file id: the SAS is asked by id (POST contracts/files/{id}/sas-token/read) instead of by path */
  contractFileId?: number;
  name: string;
  extension?: string;
  size?: number | null;
  lastModified?: string;
}

interface IProps {
  open: boolean;
  file: PreviewFile | undefined;
  onDismiss: () => void;
  /** The read SAS could not be obtained (e.g. 404: the blob no longer exists) */
  onFileError?: (path: string, error: ErrorType) => void;
}

interface ConnectedProps {
  ownerNames: StorageOwnerNames;
}

interface DispatchProps {
  RequestPreviewSas: (path: string, download: boolean) => Promise<UploadSasToken>;
  RequestContractFileSas: (fileId: number, download: boolean) => Promise<UploadSasToken>;
  FetchPreviewText: (sasUrl: string, maxBytes: number) => Promise<string>;
  ResolveEntityNames: (refs: StorageEntityRef[]) => Promise<void>;
}

export type Props = IProps & ConnectedProps & DispatchProps;

export const mapStateToProps = (state: AppState): ConnectedProps => ({
  ownerNames: selectStorageOwnerNames(state),
});

export const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): DispatchProps => ({
  RequestPreviewSas: (path, download) => dispatch(StorageBrowserStore.ActionCreators.RequestPreviewSas(path, download)),
  RequestContractFileSas: (fileId, download) => dispatch(StorageBrowserStore.ActionCreators.RequestContractFileSas(fileId, download)),
  FetchPreviewText: (sasUrl, maxBytes) => dispatch(StorageBrowserStore.ActionCreators.FetchPreviewText(sasUrl, maxBytes)),
  ResolveEntityNames: (refs) => dispatch(StorageBrowserStore.ActionCreators.ResolveEntityNames(refs)),
});

//#endregion

//#region Renderer decision (pure)

export type PreviewRenderer = 'pdf' | 'image' | 'video' | 'audio' | 'text' | 'csv' | 'office' | 'unsupported';

export type OfficeApp = 'word' | 'excel' | 'powerpoint';

/** Why a supported file is not rendered (the download is offered instead). */
export type PreviewBlockedReason = 'officeTooLarge' | 'textTooLarge';

export interface PreviewDecision {
  renderer: PreviewRenderer;
  officeApp?: OfficeApp;
  blockedReason?: PreviewBlockedReason;
  /** Size limit that applies to the renderer (bytes) */
  limitBytes?: number;
}

const MB = 1024 * 1024;

/** Text / CSV previews are read by the browser: up to 2 MB. */
export const PREVIEW_TEXT_MAX_BYTES = 2 * MB;

/** Rows of a CSV shown in the preview table. */
export const CSV_PREVIEW_MAX_ROWS = 500;

/** Microsoft Office Online viewer limits: Word / PowerPoint 10 MB, Excel 5 MB. */
export const OFFICE_VIEWER_LIMITS: Record<OfficeApp, number> = {
  word: 10 * MB,
  powerpoint: 10 * MB,
  excel: 5 * MB,
};

/** Formats documented as supported by the Office Online viewer (no OpenDocument). */
const OFFICE_EXTENSIONS: Record<string, OfficeApp> = {
  doc: 'word',
  docx: 'word',
  docm: 'word',
  dotx: 'word',
  xls: 'excel',
  xlsx: 'excel',
  xlsm: 'excel',
  xlsb: 'excel',
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  pptm: 'powerpoint',
  pps: 'powerpoint',
  ppsx: 'powerpoint',
};

const RENDERERS: Record<string, PreviewRenderer> = {
  pdf: 'pdf',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  // <img> never runs the scripts an SVG may contain
  svg: 'image',
  mp4: 'video',
  webm: 'video',
  mp3: 'audio',
  wav: 'audio',
  ogg: 'audio',
  m4a: 'audio',
  txt: 'text',
  log: 'text',
  json: 'text',
  xml: 'text',
  md: 'text',
  csv: 'csv',
};

const normalizeExtension = (extension: string | null | undefined): string =>
  (extension ?? '').trim().replace(/^\./, '').toLowerCase();

/** Preview descriptor of a contract attachment: the SAS is asked by id, the path only identifies the file in the state. */
export const contractFilePreview = (file: {
  id: number;
  fileName: string;
  fileExtension?: string | null;
  fileSize?: number | null;
  blobPath?: string | null;
  uploadDate?: Date | string | null;
}): PreviewFile => ({
  path: file.blobPath || `contract-file:${file.id}`,
  contractFileId: file.id,
  name: file.fileName,
  extension: file.fileExtension ?? undefined,
  size: typeof file.fileSize === 'number' ? file.fileSize : undefined,
  lastModified: file.uploadDate ? new Date(file.uploadDate).toISOString() : undefined,
});

/** Extension of the preview file: the one given or the one in the name. */
export const getPreviewExtension = (file: Pick<PreviewFile, 'name' | 'extension'>): string => {
  const given = normalizeExtension(file.extension);
  if (given) return given;
  const dot = (file.name ?? '').lastIndexOf('.');
  return dot > 0 ? normalizeExtension(file.name.substring(dot + 1)) : '';
};

const isKnownSize = (size: number | null | undefined): size is number =>
  typeof size === 'number' && Number.isFinite(size) && size >= 0;

/** Decides how a file is previewed from its extension (and size, for the Office / text limits). */
export const getPreviewDecision = (extension: string | null | undefined, size?: number | null): PreviewDecision => {
  const ext = normalizeExtension(extension);

  const officeApp = OFFICE_EXTENSIONS[ext];
  if (officeApp) {
    const limitBytes = OFFICE_VIEWER_LIMITS[officeApp];
    return isKnownSize(size) && size > limitBytes
      ? { renderer: 'office', officeApp, limitBytes, blockedReason: 'officeTooLarge' }
      : { renderer: 'office', officeApp, limitBytes };
  }

  const renderer = RENDERERS[ext] ?? 'unsupported';
  if (renderer === 'text' || renderer === 'csv') {
    return isKnownSize(size) && size > PREVIEW_TEXT_MAX_BYTES
      ? { renderer, limitBytes: PREVIEW_TEXT_MAX_BYTES, blockedReason: 'textTooLarge' }
      : { renderer, limitBytes: PREVIEW_TEXT_MAX_BYTES };
  }
  return { renderer };
};

/** The renderer needs a read SAS (everything but unsupported / blocked files). */
export const needsSas = (decision: PreviewDecision): boolean =>
  decision.renderer !== 'unsupported' && !decision.blockedReason;

/** The renderer reads the content as text through the browser (needs CORS GET on the storage). */
export const needsText = (decision: PreviewDecision): boolean =>
  (decision.renderer === 'text' || decision.renderer === 'csv') && !decision.blockedReason;

/**
 * Office Online viewer url for a (public, read-only, short lived) SAS url. The "embed" view is read-only for
 * Word, PowerPoint and Excel: Office Online can never write back to a url source and the SAS is read-only
 * (sp=r, short lived). The wdAllowInteractivity/AllowTyping params are OneDrive embed params that
 * view.officeapps.live.com ignores, so they are not sent.
 */
export const buildOfficeViewerUrl = (sasUrl: string, _officeApp?: OfficeApp): string =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(sasUrl)}`;

/**
 * sandbox of the Office Online iframe: the viewer is a script app served from its own origin, so it needs
 * allow-scripts + allow-same-origin (its origin, never ours). embed.aspx is only a loader that auto-submits a
 * form to the regional Excel/Word host: without allow-forms the frame stays blank. allow-popups lets it open
 * its own windows; still no allow-top-navigation or allow-modals: it cannot navigate the app.
 */
export const OFFICE_VIEWER_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

/**
 * PDF url for the inline viewer: hides the browser viewer toolbar (annotation / print tools) and side panel.
 * The PDF iframe is NOT sandboxed: Chromium refuses to render PDFs inside sandboxed frames. The SAS is read-only
 * (sp=r): any annotation a browser viewer may still allow is local and never saved.
 */
export const buildPdfViewerUrl = (sasUrl: string): string =>
  `${sasUrl.split('#')[0]}#toolbar=0&navpanes=0`;

/** Original name without the "_yyyyMMddTHHmmss" suffix the upload adds (contract §7). */
export const getFriendlyFileName = (name: string): string => {
  const value = name ?? '';
  const dot = value.lastIndexOf('.');
  const base = dot > 0 ? value.substring(0, dot) : value;
  const extension = dot > 0 ? value.substring(dot) : '';
  const stripped = base.replace(/_\d{8}T\d{6}$/, '');
  return stripped ? `${stripped}${extension}` : value;
};

//#endregion

//#region CSV (pure)

export type CsvSeparator = ',' | ';' | '\t';

/** Separator of the first line (outside quotes): the most frequent among , ; and tab (default ","). */
export const detectCsvSeparator = (text: string): CsvSeparator => {
  const counts: Record<CsvSeparator, number> = { ',': 0, ';': 0, '\t': 0 };
  let inQuotes = false;
  for (let index = 0; index < text.length; index++) {
    const char = text[index];
    if (char === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (char === '\n' || char === '\r')) break;
    else if (!inQuotes && (char === ',' || char === ';' || char === '\t')) counts[char as CsvSeparator]++;
  }
  const best = (Object.keys(counts) as CsvSeparator[]).reduce((a, b) => (counts[b] > counts[a] ? b : a), ',');
  return counts[best] > 0 ? best : ',';
};

export interface CsvPreview {
  header: string[];
  rows: string[][];
  /** There were more data rows than the ones returned */
  truncated: boolean;
  separator: CsvSeparator;
  /** Widest row (header included) */
  columnCount: number;
}

/**
 * Small RFC 4180 parser: quoted fields with separators, line breaks and "" escapes; CRLF / LF;
 * leading BOM removed; blank lines skipped. The first row is the header; up to `maxRows` data rows.
 */
export const parseCsv = (text: string, maxRows: number = CSV_PREVIEW_MAX_ROWS, separator?: CsvSeparator): CsvPreview => {
  const source = (text ?? '').replace(/^﻿/, '');
  const sep = separator ?? detectCsvSeparator(source);
  const records: string[][] = [];
  let truncated = false;

  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let fieldStarted = false;

  const pushRow = (): boolean => {
    const isBlank = row.length === 0 && field === '' && !fieldStarted;
    if (!isBlank) {
      row.push(field);
      if (records.length === maxRows + 1) {
        truncated = true;
        return false;
      }
      records.push(row);
    }
    row = [];
    field = '';
    fieldStarted = false;
    return true;
  };

  for (let index = 0; index < source.length; index++) {
    const char = source[index];
    if (inQuotes) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          field += '"';
          index++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      fieldStarted = true;
    } else if (char === sep) {
      row.push(field);
      field = '';
      fieldStarted = true;
    } else if (char === '\r' || char === '\n') {
      if (char === '\r' && source[index + 1] === '\n') index++;
      if (!pushRow()) break;
    } else {
      field += char;
      fieldStarted = true;
    }
  }
  if (!truncated) pushRow();

  const [header = [], ...rows] = records;
  const columnCount = records.reduce((max, record) => Math.max(max, record.length), 0);
  return { header, rows, truncated, separator: sep, columnCount };
};

//#endregion

//#region Errors

/** Message with the detail returned by the API / Azure (validation details first). */
export const getPreviewErrorDetail = (error: ErrorType | undefined): string => {
  if (!error) return '';
  const details = (error.Errors ?? []).map((detail) => String(detail).trim()).filter(Boolean);
  return details.length > 0 ? Array.from(new Set(details)).join(' ') : error.ErrorMessage ?? '';
};

/** i18n key of the error title by HTTP status. */
export const getPreviewErrorTitleKey = (error: ErrorType | undefined): string => {
  switch (error?.ErrorCode) {
    case 401:
    case 403:
      return 'filePreview.errors.notAuthorized';
    case 404:
      return 'filePreview.errors.notFound';
    case 0:
      return 'filePreview.errors.network';
    default:
      return 'filePreview.errors.generic';
  }
};

//#endregion

//#region Drawer width ("Expandir / Contraer", spec 005)

/** Session key of the "expanded" choice of the preview (full screen vs wide side panel). */
export const PREVIEW_EXPANDED_STORAGE_KEY = 'prebiller.filePreview.expanded';

/** Remembered choice for this browser session; false (wide side panel) when storage is unavailable. */
export const readPreviewExpanded = (): boolean => {
  try {
    return window.sessionStorage.getItem(PREVIEW_EXPANDED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/** Remembers the choice for the session; storage errors (private mode, quota) are ignored. */
export const writePreviewExpanded = (expanded: boolean): void => {
  try {
    if (expanded) window.sessionStorage.setItem(PREVIEW_EXPANDED_STORAGE_KEY, '1');
    else window.sessionStorage.removeItem(PREVIEW_EXPANDED_STORAGE_KEY);
  } catch {
    // keep working without remembering it
  }
};

/** Wide side panel by default (min(1100px, 75vw), full width under 768px); full screen when expanded. */
export const getPreviewDrawerSize = (expanded: boolean): 'wide' | 'full' => (expanded ? 'full' : 'wide');

//#endregion
